<script lang="ts">
  import Modal from '../ui/Modal.svelte';
  import FormField from '../ui/FormField.svelte';
  import Button from '../ui/Button.svelte';
  import { WEBHOOK_EVENTS, type Webhook, type WebhookEventId } from '../../lib/types';

  type Props = {
    webhook: Webhook | null;
    onClose: () => void;
    onSave: (data: { url: string; events: WebhookEventId[]; signingSecret: string }) => void;
  };

  let { webhook, onClose, onSave }: Props = $props();

  let url = $state('');
  let selectedEvents = $state<WebhookEventId[]>([]);
  let signingSecret = $state('');
  let saving = $state(false);
  let error = $state('');

  let isEdit = $derived(webhook !== null);
  let title = $derived(isEdit ? 'Edit Webhook' : 'Add Webhook');

  function validateUrl(value: string): string {
    if (value.trim().length === 0) return '';
    try {
      new URL(value.trim());
      return '';
    } catch {
      return 'Please enter a valid URL (e.g. https://your-app.com/webhooks)';
    }
  }

  let urlError = $derived(validateUrl(url));
  let isValid = $derived(url.trim().length > 0 && urlError === '' && selectedEvents.length > 0);

  // Initialize from webhook prop when editing
  $effect(() => {
    if (webhook) {
      url = webhook.url;
      selectedEvents = [...webhook.events];
      signingSecret = webhook.signingSecret ?? '';
    }
  });

  // Group events by their group field
  function buildEventGroups() {
    const groups = new Map<string, typeof WEBHOOK_EVENTS>();
    for (const event of WEBHOOK_EVENTS) {
      if (!groups.has(event.group)) {
        groups.set(event.group, []);
      }
      groups.get(event.group)!.push(event);
    }
    return groups;
  }
  const eventGroups = buildEventGroups();

  function toggleEvent(eventId: WebhookEventId) {
    if (selectedEvents.includes(eventId)) {
      selectedEvents = selectedEvents.filter(e => e !== eventId);
    } else {
      selectedEvents = [...selectedEvents, eventId];
    }
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();

    if (!url.trim()) {
      error = 'Please enter an endpoint URL';
      return;
    }

    if (urlError) {
      error = urlError;
      return;
    }

    if (selectedEvents.length === 0) {
      error = 'Please select at least one event';
      return;
    }

    error = '';
    saving = true;

    try {
      onSave({
        url: url.trim(),
        events: selectedEvents,
        signingSecret: signingSecret.trim(),
      });
    } catch (err: any) {
      error = err.message || 'Failed to save webhook';
      saving = false;
    }
  }
</script>

<Modal isOpen={true} {title} size="lg" onClose={onClose}>
  <form onsubmit={handleSubmit}>
    {#if error}
      <div class="error-message">{error}</div>
    {/if}

    <FormField label="Endpoint URL" required error={url.length > 0 ? urlError : ''}>
      <input
        type="url"
        bind:value={url}
        placeholder="https://your-app.com/webhooks"
      />
    </FormField>

    <FormField label="Events" required hint="Select which events trigger this webhook">
      <div class="event-groups">
        {#each [...eventGroups] as [groupName, events]}
          <div class="event-group">
            <h4 class="group-label">{groupName}</h4>
            <div class="checkbox-group">
              {#each events as event}
                <label class="checkbox-item" class:checked={selectedEvents.includes(event.id)}>
                  <input
                    type="checkbox"
                    checked={selectedEvents.includes(event.id)}
                    onchange={() => toggleEvent(event.id)}
                  />
                  {event.label}
                </label>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    </FormField>

    <FormField label="Signing Secret" hint="Optional. Used to verify webhook payloads with HMAC.">
      <input
        type="text"
        bind:value={signingSecret}
        placeholder="whsec_..."
      />
    </FormField>

    <div class="form-actions">
      <Button variant="secondary" onclick={onClose}>Cancel</Button>
      <Button type="submit" variant="primary" disabled={!isValid} loading={saving}>
        {isEdit ? 'Save Changes' : 'Add Webhook'}
      </Button>
    </div>
  </form>
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .error-message {
    background: #fee;
    border: 1px solid #fcc;
    color: #c33;
    padding: 0.75rem;
    border-radius: 6px;
    font-size: 0.875rem;
  }

  input[type="url"],
  input[type="text"] {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.9375rem;
    font-family: inherit;
    transition: border-color 0.2s;
  }

  input[type="url"]:focus,
  input[type="text"]:focus {
    outline: none;
    border-color: #667eea;
  }

  .event-groups {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .event-group {
    background: #f9fafb;
    border-radius: 8px;
    padding: 0.75rem 1rem;
  }

  .group-label {
    margin: 0 0 0.5rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }
</style>
